import { POWER_TYPES, VEHICLE_TYPES, type PowerType, type Vehicle, type VehicleType } from "@fts/core";
import { useMemo, useState, type FormEvent } from "react";
import { Dialog, FormError, FormField, fieldAria } from "../components/Dialog";
import { useFeedback } from "../components/Toast";
import { useI18n } from "../i18n";
import { navigate } from "../router/hash";
import { useSession, type TenantData } from "../state/session";
import { siteOptions } from "./SiteForm";

/** Allowed by the vehicles.battery_voltage check constraint. */
export const BATTERY_VOLTAGES = [12, 24, 36, 48, 72, 80, 96] as const;

export function VehicleFormDialog({
  data,
  vehicle,
  defaultSiteId,
  onClose,
}: {
  data: TenantData;
  vehicle?: Vehicle;
  defaultSiteId?: string;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const { provider, tenant, refresh } = useSession();
  const feedback = useFeedback();
  const [fleetNo, setFleetNo] = useState(vehicle?.fleetNo ?? "");
  const [type, setType] = useState<VehicleType>(vehicle?.type ?? "forklift");
  const [power, setPower] = useState<PowerType>(vehicle?.power ?? "electric");
  const [make, setMake] = useState(vehicle?.make ?? "");
  const [model, setModel] = useState(vehicle?.model ?? "");
  const [serialNo, setSerialNo] = useState(vehicle?.serialNo ?? "");
  const [battery, setBattery] = useState(vehicle?.batteryVoltage != null ? String(vehicle.batteryVoltage) : "");
  const [siteId, setSiteId] = useState(vehicle?.siteId ?? defaultSiteId ?? "");
  const [active, setActive] = useState(vehicle?.active ?? true);
  const [unauthorizedStart, setUnauthorizedStart] = useState(vehicle?.unauthorizedStartAlarm ?? true);
  // ADR 0023: the bound device has no card reader, so the alarm cannot occur. The stored setting is kept
  // unchanged (it applies again if a device with a card reader is bound later).
  const noReader = !!vehicle?.deviceId && data.devices.some((d) => d.deviceId === vehicle.deviceId && !d.hasCardReader);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const sites = useMemo(() => siteOptions(data.sites), [data.sites]);

  const fn = fleetNo.trim();
  const duplicate = data.vehicles.some((v) => v.fleetNo.toLocaleLowerCase("tr") === fn.toLocaleLowerCase("tr") && v.id !== vehicle?.id);
  const errors = {
    fleetNo: !fn ? t("form.required") : fn.length > 40 ? t("form.tooLong", { max: 40 }) : duplicate ? t("vehicleForm.fleetNoTaken") : null,
    site: !siteId ? t("form.chooseOne") : null,
  };
  const shown = (k: keyof typeof errors) => (touched ? errors[k] : null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!tenant || Object.values(errors).some(Boolean)) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await provider.saveVehicle({
        id: vehicle?.id,
        tenantId: tenant.id,
        siteId,
        fleetNo: fn,
        type,
        power,
        make: make.trim() || null,
        model: model.trim() || null,
        serialNo: serialNo.trim() || null,
        batteryVoltage: battery ? Number(battery) : null,
        active,
        unauthorizedStartAlarm: unauthorizedStart,
      });
      await refresh();
      feedback.ok(t(vehicle ? "toast.savedNamed" : "toast.addedNamed", { name: saved.fleetNo }));
      onClose();
      if (!vehicle) navigate(`/vehicles/${encodeURIComponent(saved.id)}`);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Dialog
      wide
      title={vehicle ? t("vehicleForm.editTitle", { fleetNo: vehicle.fleetNo }) : t("vehicleForm.createTitle")}
      onClose={() => !busy && onClose()}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="button button-primary" disabled={busy}>
            {busy ? t("common.working") : t("common.save")}
          </button>
        </>
      }
    >
      <div className="form-row">
        <FormField id="veh-fleet" label={t("vehicle.fleetNo")} error={shown("fleetNo")}>
          <input {...fieldAria("veh-fleet", { error: shown("fleetNo") })} className="mono" value={fleetNo} onChange={(e) => setFleetNo(e.target.value)} maxLength={40} autoComplete="off" data-autofocus />
        </FormField>
        <FormField id="veh-site" label={t("vehicle.site")} error={shown("site")}>
          <select {...fieldAria("veh-site", { error: shown("site") })} value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            <option value="">{t("form.select")}</option>
            {sites.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </FormField>
      </div>
      <div className="form-row">
        <FormField id="veh-type" label={t("vehicle.type")}>
          <select id="veh-type" value={type} onChange={(e) => setType(e.target.value as VehicleType)}>
            {VEHICLE_TYPES.map((x) => (
              <option key={x} value={x}>
                {t(`vehicleType.${x}`)}
              </option>
            ))}
          </select>
        </FormField>
        <FormField id="veh-power" label={t("vehicle.power")}>
          <select id="veh-power" value={power} onChange={(e) => setPower(e.target.value as PowerType)}>
            {POWER_TYPES.map((x) => (
              <option key={x} value={x}>
                {t(`power.${x}`)}
              </option>
            ))}
          </select>
        </FormField>
      </div>
      <div className="form-row">
        <FormField id="veh-make" label={t("vehicle.make")} optional>
          <input id="veh-make" value={make} onChange={(e) => setMake(e.target.value)} autoComplete="off" />
        </FormField>
        <FormField id="veh-model" label={t("vehicle.model")} optional>
          <input id="veh-model" value={model} onChange={(e) => setModel(e.target.value)} autoComplete="off" />
        </FormField>
      </div>
      <div className="form-row">
        <FormField id="veh-serial" label={t("vehicle.serialNo")} optional>
          <input id="veh-serial" className="mono" value={serialNo} onChange={(e) => setSerialNo(e.target.value)} autoComplete="off" />
        </FormField>
        <FormField id="veh-battery" label={t("vehicle.battery")} optional hint={t("vehicleForm.batteryHint")}>
          <select {...fieldAria("veh-battery", { hint: true })} value={battery} onChange={(e) => setBattery(e.target.value)}>
            <option value="">{t("vehicleForm.noBattery")}</option>
            {BATTERY_VOLTAGES.map((v) => (
              <option key={v} value={v}>
                {t("vehicle.batteryValue", { value: v })}
              </option>
            ))}
          </select>
        </FormField>
      </div>
      <label className="check-row">
        <input type="checkbox" role="switch" checked={active} onChange={(e) => setActive(e.target.checked)} aria-describedby="veh-active-hint" />
        <span>
          <span className="strong">{t("vehicle.active")}</span>
          <span id="veh-active-hint" className="field-hint">
            {t("vehicleForm.activeHint")}
          </span>
        </span>
      </label>
      <label className="check-row">
        <input
          type="checkbox"
          role="switch"
          checked={unauthorizedStart && !noReader}
          disabled={noReader}
          onChange={(e) => setUnauthorizedStart(e.target.checked)}
          aria-describedby="veh-unauth-hint"
        />
        <span>
          <span className="strong">{t("vehicleForm.unauthorizedStart")}</span>
          <span id="veh-unauth-hint" className="field-hint">
            {noReader ? t("vehicleForm.unauthorizedStartNoReader") : t("vehicleForm.unauthorizedStartHint")}
          </span>
        </span>
      </label>
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}
